import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Banner, Button, Icon, Modal, Spinner, Text, TextField, Tooltip } from '@shopify/polaris';
import {
    DragHandleIcon,
    ListBulletedIcon,
    SocialPostIcon,
    MagicIcon
} from '@shopify/polaris-icons';
import {
    DndContext,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    pointerWithin,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Language from '../../../../../../i18n/Language';
import {
    SCHEMA_EDITOR_CONFIG,
    SCHEMA_EDITOR_TYPE,
    createEmptySchemaItem,
    getSchemaFieldError,
    getSchemaListKey,
    isSchemaItemValid,
    normalizeSchemaContent,
    toSchemaApiBody,
    toSchemaEditorPayload,
    validateSchemaContent,
} from './schemaEditorModel';
import './schemaContentEditor.scss';
const t = Language;

// 指针落在行内时优先命中该行；键盘拖拽没有指针，再退回中心点距离。
function schemaCollisionDetection(args) {
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length > 0) return pointerCollisions;
    return closestCenter(args);
}

function restrictToVerticalAxis({ transform }) {
    return { ...transform, x: 0 };
}

function fieldInputLabel(editorType, field, index) {
    if (editorType === SCHEMA_EDITOR_TYPE.howto && field.key === 'title') {
        return t('Step {{v1}}', index + 1);
    }
    return t(field.label);
}

function formatFieldError(error, revealed) {
    if (!error) return false;
    if (error.code === 'required' && !revealed) return false;
    return t(error.key, t(error.label), error.maxLength);
}

const SortableSchemaRow = React.memo(function SortableSchemaRow({
    item,
    index,
    editorType,
    fields,
    isEditing,
    revealed,
    onEdit,
    onConfirm,
    onDelete,
    onChange,
}) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: item.id });

    const style = {
        transform: CSS.Translate.toString(transform),
        transition,
        zIndex: isDragging ? 2 : undefined,
    };

    const primary = item[fields[0].key];
    const secondary = item[fields[1].key];

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`schema_editor_item${isEditing ? ' is_editing' : ''}${isDragging ? ' is_dragging' : ''}`}
        >
            <div className="schema_editor_item_main">
                <div
                    className="schema_editor_drag"
                    aria-label={t('Drag to reorder')}
                    {...attributes}
                    {...listeners}
                >
                    <Icon source={DragHandleIcon} tone="base" />
                </div>
                <div className="schema_editor_item_body">
                    {isEditing ? (
                        <>
                            <div className="schema_editor_fields">
                                {fields.map((field) => {
                                    const message = formatFieldError(getSchemaFieldError(field, item[field.key]), revealed);
                                    return (
                                        <TextField
                                            key={field.key}
                                            label={fieldInputLabel(editorType, field, index)}
                                            value={item[field.key] || ''}
                                            placeholder={t(field.placeholder)}
                                            autoComplete="off"
                                            error={message}
                                            onChange={(value) => onChange(item.id, field.key, value)}
                                        />
                                    );
                                })}
                            </div>
                            <div className="schema_editor_edit_actions">
                                <Button tone="critical" onClick={() => onDelete(item.id)}>{t('Delete')}</Button>
                                <Button onClick={() => onConfirm(item.id)}>{t('Save')}</Button>
                            </div>
                        </>
                    ) : (
                        // 点击整个条目展开编辑
                        <div
                            className="schema_editor_preview"
                            role="button"
                            tabIndex={0}
                            onClick={() => onEdit(item.id)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    onEdit(item.id);
                                }
                            }}
                        >
                            <Text as="p" variant="bodyMd" fontWeight="medium">{primary}</Text>
                            <Text as="p" variant="bodyMd" tone="subdued" truncate>{secondary}</Text>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});

export default function SchemaContentEditor({
    editorType,
    content,
    saveLoading = false,
    generating = false,
    aiGenerated = false,
    showEmptyBanner = false,
    onClose,
    onSave,
    onGenerate,
    onDismissEmptyBanner,
}) {
    const config = SCHEMA_EDITOR_CONFIG[editorType];
    const listKey = getSchemaListKey(editorType);
    const [draft, setDraft] = useState(() => normalizeSchemaContent(editorType, content));
    // 支持多条同时处于编辑态。AI 生成成功后（aiGenerated）默认展开所有条目为可编辑输入框，方便逐条检查/修改。
    const [editingIds, setEditingIds] = useState(() => {
        if (!aiGenerated) return new Set();
        return new Set((draft[listKey] || []).map((item) => item.id));
    });
    const [revealedIds, setRevealedIds] = useState(() => new Set());
    const [revealAll, setRevealAll] = useState(false);
    const [isSorting, setIsSorting] = useState(false);
    // 关闭/离开编辑弹窗时，若有未保存修改则弹出二次确认
    const [showUnsavedConfirm, setShowUnsavedConfirm] = useState(false);

    const draftRef = useRef(draft);
    draftRef.current = draft;
    const editingIdsRef = useRef(editingIds);
    editingIdsRef.current = editingIds;

    // 打开时的原始内容基线（按"实际提交 body"序列化，忽略 id/顺序外的无关差异）。
    // content 变化会通过父级 key 触发重挂，基线随之以新内容重建。
    const baselineBodyRef = useRef(null);
    if (baselineBodyRef.current === null) {
        baselineBodyRef.current = JSON.stringify(
            toSchemaApiBody(toSchemaEditorPayload(editorType, normalizeSchemaContent(editorType, content)))
        );
    }

    const items = draft[listKey];
    // 只跟 id 顺序有关。输入内容变化时保持同一数组，避免拖拽上下文把整列打回去重渲染。
    const itemIdKey = items.map((item) => item.id).join('\0');
    const itemIds = useMemo(() => (itemIdKey ? itemIdKey.split('\0') : []), [itemIdKey]);
    const atLimit = items.length >= config.maxItems;

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const revealItem = useCallback((id) => {
        setRevealedIds((prev) => {
            if (prev.has(id)) return prev;
            const next = new Set(prev);
            next.add(id);
            return next;
        });
    }, []);

    const closeEditing = useCallback((id) => {
        setEditingIds((prev) => {
            if (!prev.has(id)) return prev;
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
    }, []);

    const updateItemField = useCallback((id, key, value) => {
        setDraft((prev) => ({
            ...prev,
            [listKey]: prev[listKey].map((item) => (item.id === id ? { ...item, [key]: value } : item)),
        }));
    }, [listKey]);

    const updateMetaField = useCallback((key, value) => {
        setDraft((prev) => ({ ...prev, [key]: value }));
    }, []);

    // 校验当前处于编辑态且无效的其它条目（用于互斥切换前拦截）
    const findBlockingEditing = useCallback((exceptId) => {
        const list = draftRef.current[listKey] || [];
        return [...editingIdsRef.current].filter((eid) => {
            if (eid === exceptId) return false;
            const item = list.find((it) => it.id === eid);
            return item && !isSchemaItemValid(editorType, item);
        });
    }, [editorType, listKey]);

    // 点击编辑：恢复互斥——只展开当前条目，收起其余（AI 生成后的多条同时编辑仅停留在生成那一刻）
    const handleEdit = useCallback((id) => {
        const blocking = findBlockingEditing(id);
        if (blocking.length) {
            blocking.forEach((eid) => revealItem(eid));
            return;
        }
        setEditingIds(new Set([id]));
    }, [findBlockingEditing, revealItem]);

    const handleConfirm = useCallback((id) => {
        const current = (draftRef.current[listKey] || []).find((item) => item.id === id);
        if (!current) return;
        revealItem(id);
        if (isSchemaItemValid(editorType, current)) {
            closeEditing(id);
        }
    }, [editorType, listKey, revealItem, closeEditing]);

    const handleDelete = useCallback((id) => {
        setDraft((prev) => ({
            ...prev,
            [listKey]: prev[listKey].filter((item) => item.id !== id),
        }));
        closeEditing(id);
        setRevealedIds((prev) => {
            if (!prev.has(id)) return prev;
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
    }, [listKey, closeEditing]);

    const handleAdd = useCallback(() => {
        const list = draftRef.current[listKey] || [];
        if (list.length >= config.maxItems) return;
        const blocking = findBlockingEditing(null);
        if (blocking.length) {
            blocking.forEach((eid) => revealItem(eid));
            return;
        }
        const nextItem = createEmptySchemaItem(editorType);
        setDraft((prev) => ({
            ...prev,
            [listKey]: [...prev[listKey], nextItem],
        }));
        // 互斥：新增条目成为唯一处于编辑态的条目
        setEditingIds(new Set([nextItem.id]));
    }, [config.maxItems, editorType, listKey, findBlockingEditing, revealItem]);

    const handleDeleteAll = useCallback(() => {
        setDraft((prev) => {
            const next = { ...prev, [listKey]: [] };
            (config.metaFields || []).forEach((field) => { next[field.key] = ''; });
            return next;
        });
        setEditingIds(new Set());
        setRevealedIds(new Set());
        setRevealAll(false);
    }, [config.metaFields, listKey]);

    const handleDragEnd = useCallback((event) => {
        setIsSorting(false);
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        setDraft((prev) => {
            const list = prev[listKey];
            const oldIndex = list.findIndex((item) => item.id === active.id);
            const newIndex = list.findIndex((item) => item.id === over.id);
            if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return prev;
            return {
                ...prev,
                [listKey]: arrayMove(list, oldIndex, newIndex),
            };
        });
    }, [listKey]);

    const handleSave = () => {
        const result = validateSchemaContent(editorType, draftRef.current);
        if (!result.ok) {
            setRevealAll(true);
            if (result.invalidIds.length) {
                setRevealedIds((prev) => {
                    const next = new Set(prev);
                    result.invalidIds.forEach((id) => next.add(id));
                    return next;
                });
            }
            // 互斥：只展开第一条无效条目
            if (result.firstInvalidItemId) {
                setEditingIds(new Set([result.firstInvalidItemId]));
            }
            return;
        }
        onSave(toSchemaEditorPayload(editorType, draftRef.current));
    };

    const listMessage = revealAll && items.length === 0 ? t(config.emptyTip) : '';
    const limitMessage = atLimit ? t(config.limitTip) : '';

    // 与源数据对比：提交 body 一致则无改动，Save 置灰
    const currentBodyKey = JSON.stringify(toSchemaApiBody(toSchemaEditorPayload(editorType, draft)));
    const isDirty = currentBodyKey !== baselineBodyRef.current;

    // 请求关闭：有未保存修改则先二次确认，否则直接关闭
    const requestClose = () => {
        if (saveLoading) return;
        if (isDirty) {
            setShowUnsavedConfirm(true);
        } else {
            onClose && onClose();
        }
    };
    // 确认放弃修改并关闭
    const handleDiscardClose = () => {
        setShowUnsavedConfirm(false);
        onClose && onClose();
    };
    // 继续编辑（仅关闭确认弹窗）
    const handleKeepEditing = () => setShowUnsavedConfirm(false);

    // 关闭
    const handleCloseFaqHowToBanner = (type) => {
        if (type === 'faq') {
            // setIsShowFaqBanner(false);
        } else {
            // setIsShowHowToBanner(false);
        }
    }

    return (
        <>
            <Modal
                open
                title={t(config.modalTitle)}
                onClose={saveLoading ? undefined : requestClose}
                primaryAction={{
                    content: t('Save'),
                    onAction: handleSave,
                    loading: saveLoading,
                    disabled: generating || !isDirty,
                }}
                secondaryActions={[{
                    content: t('Cancel'),
                    onAction: requestClose,
                    disabled: saveLoading,
                }]}
            >
                <Modal.Section>
                    <div className={`schema_content_editor${editorType === SCHEMA_EDITOR_TYPE.howto ? ' is_howto' : ''}${aiGenerated ? ' is_ai' : ''}`}>
                        <div className="schema_editor_section_head">
                            <Text as="h3" variant="headingSm">{t(config.sectionTitle)}</Text>
                            {onGenerate ? (
                                <div className="schema_editor_generate">
                                    <Tooltip content={t("Generating will replace your existing content and consume {{v1}} AI credits each time.", 0.1)}>
                                        <Button
                                            disabled={saveLoading || generating}
                                            onClick={onGenerate}
                                        >
                                            <span className="schema_editor_generate_content">
                                                {generating ? <Spinner size="small" /> : <Icon source={MagicIcon} />}
                                                <span>{t('Generate')}</span>
                                            </span>
                                        </Button>
                                    </Tooltip>
                                </div>
                            ) : null}
                        </div>
                        {showEmptyBanner ? (
                            <div className="schema_editor_banner">
                                <Banner
                                    tone='info'
                                    title={t(editorType === SCHEMA_EDITOR_TYPE.faq
                                        ? "Cannot generate FAQ. Please add FAQ sections to your page content first."
                                        : "Cannot generate HowTo schema. Please add HowTo sections to your page content first.")}
                                    onDismiss={onDismissEmptyBanner}
                                ></Banner>
                            </div>
                        ) : null}
                        {config.metaFields ? (
                            <div className="schema_editor_meta">
                                {config.metaFields.map((field) => {
                                    const message = formatFieldError(getSchemaFieldError(field, draft[field.key]), revealAll);
                                    return (
                                        <TextField
                                            key={field.key}
                                            label={t(field.label)}
                                            value={draft[field.key] || ''}
                                            placeholder={t(field.placeholder)}
                                            autoComplete="off"
                                            error={message}
                                            onChange={(value) => updateMetaField(field.key, value)}
                                        />
                                    );
                                })}
                            </div>
                        ) : null}
                        <div className="schema_editor_steps">
                            {config.listTitle ? <Text as="h4" variant="headingXs">{t(config.listTitle)}</Text> : null}
                            <div className={`schema_editor_list${isSorting ? ' is_sorting' : ''}${items.length ? ' has_items' : ' is_empty'}`}>
                                {items.length === 0 ? (
                                    <div className="schema_editor_empty">
                                        <Text as="p" variant="bodyMd" fontWeight="semibold">{t(config.emptyTitle)}</Text>
                                        <Text as="p" variant="bodySm" tone="subdued">{t(config.emptyDesc)}</Text>
                                        <div className="schema_editor_empty_action">
                                            <Button disabled={saveLoading} onClick={handleAdd}>
                                                {t(config.addLabel)}
                                            </Button>
                                        </div>
                                        {listMessage ? <Text as="p" variant="bodyMd" tone="critical">{listMessage}</Text> : null}
                                    </div>
                                ) : (
                                    <>
                                        <DndContext
                                            sensors={sensors}
                                            collisionDetection={schemaCollisionDetection}
                                            modifiers={[restrictToVerticalAxis]}
                                            onDragStart={() => setIsSorting(true)}
                                            onDragCancel={() => setIsSorting(false)}
                                            onDragEnd={handleDragEnd}
                                        >
                                            <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
                                                {items.map((item, index) => (
                                                    <SortableSchemaRow
                                                        key={item.id}
                                                        item={item}
                                                        index={index}
                                                        editorType={editorType}
                                                        fields={config.itemFields}
                                                        isEditing={editingIds.has(item.id)}
                                                        revealed={revealAll || revealedIds.has(item.id)}
                                                        onEdit={handleEdit}
                                                        onConfirm={handleConfirm}
                                                        onDelete={handleDelete}
                                                        onChange={updateItemField}
                                                    />
                                                ))}
                                            </SortableContext>
                                        </DndContext>
                                        <div className="schema_editor_list_footer">
                                            <div className="schema_editor_footer_actions">
                                                <Button disabled={atLimit || saveLoading} onClick={handleAdd}>
                                                    {t(config.addLabel)}
                                                </Button>
                                                <Button disabled={saveLoading} variant="plain" tone="critical" onClick={handleDeleteAll}>
                                                    {t('Delete All')}
                                                </Button>
                                            </div>
                                            {limitMessage ? <Text as="p" variant="bodyMd" tone="subdued">{limitMessage}</Text> : null}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </Modal.Section>
            </Modal>
            <Modal
                open={showUnsavedConfirm}
                size="small"
                title={t('Unsaved changes')}
                onClose={handleKeepEditing}
                primaryAction={{
                    content: t('Keep editing'),
                    onAction: handleKeepEditing,
                }}
                secondaryActions={[{
                    content: t('Discard'),
                    onAction: handleDiscardClose,
                }]}
            >
                <Modal.Section>
                    <Text as="p" variant="bodyMd">
                        {t('You have unsaved changes. If you leave now, your changes will be lost.')}
                    </Text>
                </Modal.Section>
            </Modal>
        </>
    );
}
